import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  InputNumber,
  Modal,
  Popover,
  Spin,
  message,
} from 'antd';
import dayjs from 'dayjs';
import orderService from '../../../../services/orderService';
import {
  canAssignAltcasePrice,
  computeWeightedLotPrice,
  getEffectiveLotAvailableQty,
  getPendingTransferAllocations,
  getRemainingOrderQty,
  hasAltcasePrice,
  hasPendingLotSelection,
  maxAllocatableFromLot,
  mergeLotsForPicker,
  resolveTransferBeforePayment,
  sumAllocationQty,
} from './orderActionHelpers';

const formatDate = (value) => (value ? dayjs(value).format('DD MMM YYYY') : '—');

const formatCurrency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(n);
};

function orderRowId(row) {
  return row?.id ?? row?.uuid;
}

function lotId(lot) {
  return lot?.id ?? lot?.uuid;
}

export default function AltcasePriceCell({ row, canManage, onAssigned }) {
  const [open, setOpen] = useState(false);
  const [lots, setLots] = useState([]);
  const [loadingLots, setLoadingLots] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [qtyByLotId, setQtyByLotId] = useState({});

  const eligible =
    canManage &&
    canAssignAltcasePrice(row.status, resolveTransferBeforePayment(row)) &&
    row.product_uuid;
  const remainingQty = getRemainingOrderQty(row);
  const isChangeMode = hasAltcasePrice(row) || hasPendingLotSelection(row);
  const pendingAllocations = getPendingTransferAllocations(row);

  const initPicker = (availableLots) => {
    if (pendingAllocations.length > 0) {
      const ids = [];
      const qtyMap = {};
      for (const alloc of pendingAllocations) {
        const id = alloc?.inventory_id;
        const qty = Number(alloc?.quantity);
        if (!id || !Number.isFinite(qty) || qty <= 0) continue;
        ids.push(id);
        qtyMap[id] = qty;
      }
      setSelectedIds(ids);
      setQtyByLotId(qtyMap);
      return;
    }

    setSelectedIds([]);
    setQtyByLotId({});
    if (availableLots?.length === 1 && remainingQty > 0) {
      const id = lotId(availableLots[0]);
      const effectiveAvail = getEffectiveLotAvailableQty(availableLots[0], row, lotId);
      setSelectedIds([id]);
      setQtyByLotId({
        [id]: maxAllocatableFromLot(id, effectiveAvail, [], {}, remainingQty),
      });
    }
  };

  const loadLots = async () => {
    if (!row.product_uuid) return;
    setLoadingLots(true);
    try {
      const hasPending = hasPendingLotSelection(row);
      const data = await orderService.getRealInventoryLots(
        row.product_uuid,
        hasPending ? { only_available: 'false' } : undefined
      );
      const loaded = mergeLotsForPicker(data?.lots ?? [], row, lotId);
      setLots(loaded);
      initPicker(loaded);
    } catch (e) {
      message.error(
        e?.response?.data?.message || e?.message || 'Failed to load inventory lots'
      );
      setLots([]);
    } finally {
      setLoadingLots(false);
    }
  };

  const toggleLot = (inventoryId, checked) => {
    const lot = lots.find((l) => lotId(l) === inventoryId);
    if (!lot) return;

    if (!checked) {
      setSelectedIds((prev) => prev.filter((id) => id !== inventoryId));
      setQtyByLotId((prev) => {
        const next = { ...prev };
        delete next[inventoryId];
        return next;
      });
      return;
    }

    setSelectedIds((prevIds) => {
      setQtyByLotId((prevQty) => {
        const effectiveAvail = getEffectiveLotAvailableQty(lot, row, lotId);
        const defaultQty = maxAllocatableFromLot(
          inventoryId,
          effectiveAvail,
          prevIds,
          prevQty,
          remainingQty
        );
        return {
          ...prevQty,
          [inventoryId]: defaultQty > 0 ? defaultQty : undefined,
        };
      });
      if (prevIds.includes(inventoryId)) return prevIds;
      return [...prevIds, inventoryId];
    });
  };

  const handleClearSelection = async (oid) => {
    setAssigning(true);
    try {
      await orderService.clearAltcasePrice(oid);
      message.success('Lot selection cleared');
      setOpen(false);
      await onAssigned?.();
    } catch (e) {
      message.error(
        e?.response?.data?.message || e?.message || 'Failed to clear lot selection'
      );
    } finally {
      setAssigning(false);
    }
  };

  const handleApply = async () => {
    const oid = orderRowId(row);
    if (!oid) {
      message.error('Order id missing');
      return;
    }

    if (selectedIds.length === 0) {
      if (!hasPendingLotSelection(row)) {
        message.warning('Select at least one inventory lot');
        return;
      }

      Modal.confirm({
        title: 'Clear lot selection?',
        content:
          'This releases reserved inventory and clears the Altcase price selection.',
        okText: 'Clear selection',
        okButtonProps: { danger: true },
        onOk: () => handleClearSelection(oid),
      });
      return;
    }

    const allocations = selectedIds.map((inventory_id) => ({
      inventory_id,
      quantity: Number(qtyByLotId[inventory_id]),
    }));

    const total = allocations.reduce((s, a) => s + (a.quantity || 0), 0);
    if (!allocations.every((a) => a.quantity > 0)) {
      message.warning('Enter quantity for each selected lot');
      return;
    }
    if (Math.abs(total - remainingQty) > 0.0001) {
      message.warning(
        `Allocated qty (${total}) must equal order remaining qty (${remainingQty})`
      );
      return;
    }

    setAssigning(true);
    try {
      const assignResult = await orderService.assignAltcasePrice(oid, { allocations });

      const fromResponse = Number(assignResult?.altcase_price);
      const assignedPrice =
        Number.isFinite(fromResponse) && fromResponse > 0
          ? fromResponse
          : computeWeightedLotPrice(selectedIds, qtyByLotId, lots, lotId);

      if (assignedPrice != null && Number.isFinite(assignedPrice) && assignedPrice > 0) {
        const currentRm = row.rm_price;
        const rmBlank =
          currentRm == null ||
          currentRm === '' ||
          !(Number.isFinite(Number(currentRm)) && Number(currentRm) > 0);

        if (rmBlank) {
          try {
            await orderService.updateRmPrice(oid, assignedPrice, { only_if_blank: true });
            message.success('Altcase and RM prices updated');
          } catch (rmErr) {
            message.warning(
              rmErr?.response?.data?.message ||
                rmErr?.message ||
                'Altcase price updated, but failed to set RM price'
            );
          }
        } else {
          message.success('Altcase price updated');
        }
      } else {
        message.success('Altcase price updated');
      }

      setOpen(false);
      await onAssigned?.();
    } catch (e) {
      message.error(
        e?.response?.data?.message || e?.message || 'Failed to assign Altcase price'
      );
    } finally {
      setAssigning(false);
    }
  };

  if (!eligible) {
    return hasAltcasePrice(row) ? formatCurrency(row.altcase_price) : '—';
  }

  const allocatedTotal = sumAllocationQty(selectedIds, qtyByLotId);
  const qtyMatches = Math.abs(allocatedTotal - remainingQty) <= 0.0001;
  const canApplyEmptyClear = selectedIds.length === 0 && hasPendingLotSelection(row);

  const pickerContent = (
    <div className="w-80 max-h-80 flex flex-col">
      <div className="flex-shrink-0 pb-2">
        <div className="text-xs text-gray-500 mb-2 block">
          {isChangeMode ? (
            <span>
              Review or change lot selection. Allocate <strong>{remainingQty}</strong>{' '}
              shares total (weighted avg price), or uncheck all and Apply to clear.
            </span>
          ) : (
            <span>
              Remaining qty to allocate: <strong>{remainingQty}</strong> (weighted avg
              price)
            </span>
          )}
        </div>
        <div
          className={`text-xs block ${qtyMatches || canApplyEmptyClear ? 'text-gray-600' : 'text-amber-700'}`}
        >
          Allocated: <strong>{allocatedTotal}</strong> / <strong>{remainingQty}</strong>
          {canApplyEmptyClear ? (
            <span className="text-amber-700"> · Apply to clear selection</span>
          ) : null}
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        {loadingLots ? (
          <div className="flex justify-center py-4">
            <Spin size="small" />
          </div>
        ) : lots.length === 0 ? (
          <p className="text-xs text-gray-500">No available lots for this product.</p>
        ) : (
          <div className="flex flex-col gap-2 pr-1">
            {lots.map((lot) => {
              const id = lotId(lot);
              const checked = selectedIds.includes(id);
              const effectiveAvail = getEffectiveLotAvailableQty(lot, row, lotId);
              const lotMax = maxAllocatableFromLot(
                id,
                effectiveAvail,
                selectedIds,
                qtyByLotId,
                remainingQty
              );
              return (
                <div key={id} className="border border-gray-100 rounded p-2">
                  <Checkbox
                    checked={checked}
                    onChange={(e) => toggleLot(id, e.target.checked)}
                  >
                    <span className="text-xs">
                      {formatCurrency(lot.supplier_price)} · avail {effectiveAvail}
                    </span>
                  </Checkbox>
                  <div className="text-xs text-gray-500 pl-6">
                    {lot.supplier_name} · {formatDate(lot.inventory_date)}
                  </div>
                  {checked ? (
                    <div className="pl-6 mt-1">
                      <InputNumber
                        size="small"
                        min={0.01}
                        max={lotMax > 0 ? lotMax : undefined}
                        value={qtyByLotId[id]}
                        onChange={(v) =>
                          setQtyByLotId((prev) => ({ ...prev, [id]: v }))
                        }
                        className="w-full"
                      />
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="flex-shrink-0 flex justify-end gap-2 mt-3 pt-2 border-t border-gray-100 bg-white">
        <Button size="small" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button
          size="small"
          type="primary"
          loading={assigning}
          disabled={loadingLots || (lots.length === 0 && !canApplyEmptyClear)}
          onClick={handleApply}
        >
          Apply
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) loadLots();
      }}
      trigger="click"
      content={pickerContent}
      placement="bottomLeft"
    >
      <div className="flex flex-col gap-0.5 min-w-[120px]">
        {hasAltcasePrice(row) ? (
          <span className="text-sm">{formatCurrency(row.altcase_price)}</span>
        ) : null}
        <Button type="link" size="small" className="h-auto p-0 text-left">
          {isChangeMode ? 'Change lots' : 'Select lots'}
        </Button>
      </div>
    </Popover>
  );
}
